/* eslint-disable @typescript-eslint/no-explicit-any */
import { createApiRef } from '@backstage/core-plugin-api';
import {
  MeasurementResource,
  MeasurementState,
  Task,
  YardstickIssues,
  YardstickResults,
  AutofixStepResult,
  AutofixStepExecutionOutput,
  AutofixV2ExecuteStepRequest,
  DownloadRequestOptions,
  DownloadRequestResponse,
  DownloadStatusResponse,
  YardstickIssuesV2,
  Visibility,
  FindingsSummaryRequest,
  FindingsSummaryResponse,
  FindingsListRequest,
  FindingsListResponse,
  FindingDetailRequest,
  FindingDetailResponse,
  EntitySummaryRequest,
  EntitySummaryResponse,
} from '@internal/yardstick-common';
import { CategoryNode } from '../utils/category';
import { MetricData } from '../InsightsAndActions/charts/components/trend-view';

export interface GetMeasurementResultsOptions {
  entities?: string[];
  measurements?: string[];
  includeDetails?: boolean;
  adminShowIssues?: boolean;
  visibility?: Visibility[];
}

// TODO: Get these types from some sort of common package for Yardstick
export interface Measurement {
  id: string;
  on: any[];
  title: string;
  updated_at: string;
  created_at: string;
  domain_id: string;
  filter: any[];
  documentation: any;
  actions: any;
  checks: string[];
  description: string;
  state: MeasurementState;
}

export interface Check {
  id: string;
  title: string;
  description: string;
  created_at: string;
  updated_at: string;
  url: string;
  inputs: string[];
  domain_id: string;
  resources?: MeasurementResource[];
}

export interface CheckResult {
  result: {
    value: 'passed' | 'failed' | 'skipped' | 'unknown' | 'error';
    issues: any[];
    message: string;
    _meta: Record<string, any>;
  };
  check_id: string;
  check: Check;
  entityRef: string;
}

export interface ResultsV1 {
  data: YardstickResults;
  _meta: {
    total: number;
  };
}

export interface MeasurementResult {
  id: string;
  measurement_id: string;
  measurement: Measurement;
  entity_ref: string;
  result: {
    value: true;
    checks: CheckResult[];
  };
  created_at: string;
  updated_at: string;
}

export interface RefreshOptions {
  entity: string;
  measurements?: string[];
}

export interface RefreshResponse {
  taskId: string;
  _links: {
    self: { href: string };
    status: { href: string };
  };
}

export interface SimulateOptions {
  type: 'github:pull_request';
  data: any;
}

export interface GetExceptionsOptions {
  entities?: string[];
  scope?: string;
}

export interface ExceptionResult {
  id: number;
  requester: string;
  owner: string;
  state: string;
  scope: {
    system: string;
    subsystem: string;
    value: string;
    ref: string;
  };
  entity: {
    system: string;
    subsystem: string;
    value: string;
    ref: string;
  };
  justification: string;
  created_at: string;
  updated_at: string;
}

export interface GetIssuesV1Result {
  entities: YardstickIssues;
  _meta: { total: number; failedChunks?: number };
}

export interface GetIssuesV2Result {
  entities: YardstickIssuesV2;
  _meta: {
    totalIssues: number;
    totalGroups: number;
    ungroupedIssues: number;
    groupedIssues: number;
    failedChunks?: number;
  };
}

export interface GetChecksV1 {
  data: Check[];
}

export const yardstickApiRef = createApiRef<YardstickApi>({
  id: 'plugin.yardstick.service',
});
export interface YardstickApi {
  getMeasurementResults: (
    options?: GetMeasurementResultsOptions,
  ) => Promise<ResultsV1>;
  getIssuesV1: (
    entities: string[],
    options?: {
      focus?: string[];
      severity?: string[];
      measurementChecks?: string[];
    },
    adminShowIssues?: boolean,
    volumeTesting?: boolean,
    visibility?: Visibility[],
  ) => Promise<GetIssuesV1Result>;
  getIssuesV2: (
    entities: string[],
    options?: {
      focus?: string[];
      severity?: string[];
      measurementChecks?: string[];
      grouping?: 'none' | 'check';
    },
    adminShowIssues?: boolean,
    volumeTesting?: boolean,
    visibility?: Visibility[],
  ) => Promise<GetIssuesV2Result>;
  refresh: (options: RefreshOptions) => Promise<RefreshResponse>;
  getTaskStatus: (taskId: string) => Promise<Task>;
  simulate: (options: SimulateOptions) => Promise<any>;
  getExceptions: (options?: GetExceptionsOptions) => Promise<ExceptionResult[]>;
  getMeasurementRegistryResults: () => Promise<CategoryNode[]>;
  getEntityPageRegistryResults: () => Promise<CategoryNode[]>;
  getMeasurements: () => Promise<Measurement[]>;
  getChecks: () => Promise<GetChecksV1>;
  getTrendMetrics: (
    entities: string[],
    focus: string[],
  ) => Promise<MetricData[]>;
  // AutoFix V2 methods
  getAutofixV2StepResults: (issueId: string) => Promise<AutofixStepResult[]>;
  executeAutofixV2Step: (
    issueId: string,
    request: AutofixV2ExecuteStepRequest,
  ) => Promise<AutofixStepExecutionOutput>;
  executeAutofixV2StepBulk: (
    issueIds: string[],
    stepName: string,
    params?: Record<string, any>,
    userToken?: string,
  ) => Promise<AutofixStepExecutionOutput>;
  // Async download
  requestDownload: (
    options: DownloadRequestOptions,
  ) => Promise<DownloadRequestResponse>;
  checkDownloadStatus: (
    requestId: string,
    signal?: AbortSignal,
  ) => Promise<DownloadStatusResponse>;
  streamDownload: (requestId: string, signal?: AbortSignal) => Promise<Blob>;
  getIssuesSummary: (
    request: FindingsSummaryRequest,
  ) => Promise<FindingsSummaryResponse>;
  getIssuesList: (
    request: FindingsListRequest,
    signal?: AbortSignal,
  ) => Promise<FindingsListResponse>;
  getIssuesDetail: (
    request: FindingDetailRequest,
  ) => Promise<FindingDetailResponse>;
  getEntitySummary: (
    request: EntitySummaryRequest,
    signal?: AbortSignal,
  ) => Promise<EntitySummaryResponse>;
  deferBudget: (options: {
    deferEndpoint: string;
    entityRef: string;
  }) => Promise<void>;
  markAsRemediated: (options: {
    entityRef: string;
    issueId: string;
  }) => Promise<void>;
  undoMarkAsRemediated: (options: {
    entityRef: string;
    issueId: string;
  }) => Promise<void>;
}
